/**
 * Filtering and counting for the Users page KPI strip, kept out of the component so it can be
 * tested -- web/ is plain JS, so a build proves nothing about this arithmetic.
 *
 * Everything here reads the membership row for ONE organization, never the user's global role.
 * A user can belong to several organizations with a different role in each, so "how many
 * managers" is only a question about a particular organization's membership rows. That is also
 * why nothing here consults users_user_roles: since 2026-07-30 promoting someone to owner
 * writes only user_organizations, so a manager of this org is usually `user` globally.
 *
 * `created_at` and `source` arrived on user_organizations on 2026-09-18 and were deliberately
 * not back-filled, so every older membership carries NULL in both. Nothing here filters on a
 * date -- `created_at` is read for one number, the "today" line on the invite-signup tile --
 * but the NULLs still set two rules:
 *
 *   - a NULL source counts as staff-added, because that is what almost all of them are and
 *     because the one thing we must never do is let it inflate the invite-link number.
 *   - a NULL created_at is never "today". An undated membership is old by definition; the
 *     column only started recording on the day it was added.
 */
import { isSameDay } from 'date-fns';
import { isManagerMembership } from 'src/utils/roles';

export const ROLE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'manager', label: 'Managers' },
  { value: 'volunteer', label: 'Volunteers' },
];

export const SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'invite_link', label: 'Invite link' },
  { value: 'staff', label: 'Added by staff' },
];

export const DEFAULT_USER_FILTERS = { role: 'all', source: 'all' };

/**
 * The user's membership row for the organization being viewed, or undefined.
 *
 * Ids are compared as strings because the selected organization comes from context as a
 * number and the row carries whatever Hasura sent.
 */
export const orgMembership = (row, organizationId) =>
  row?.users_user_organizations?.find(
    (uo) => String(uo?.user_organizations_organization?.organization_id) === String(organizationId)
  );

/** Self signup is the only thing that departs from the column default, so NULL is not one. */
export const isInviteSignup = (membership) => membership?.source === 'invite_link';

/** Calendar day, not the last 24 hours -- "today" on a dashboard means since midnight. */
const joinedToday = (membership, now) => {
  const joined = membership?.created_at;
  if (!joined) return false;
  const joinedAt = new Date(joined);
  return !Number.isNaN(joinedAt.getTime()) && isSameDay(joinedAt, now);
};

export const filterUsers = (users = [], { organizationId, role, source }) =>
  users.filter((user) => {
    const membership = orgMembership(user, organizationId);
    if (!membership) return false;

    if (role === 'manager' && !isManagerMembership(membership)) return false;
    if (role === 'volunteer' && isManagerMembership(membership)) return false;

    if (source === 'invite_link' && !isInviteSignup(membership)) return false;
    if (source === 'staff' && isInviteSignup(membership)) return false;

    return true;
  });

/** Counts for the tiles. Always call it with the rows the table is showing. */
export const summarize = (users = [], organizationId, now = new Date()) => {
  let managers = 0;
  let inviteSignups = 0;
  let inviteSignupsToday = 0;

  users.forEach((user) => {
    const membership = orgMembership(user, organizationId);
    if (!membership) return;
    if (isManagerMembership(membership)) managers += 1;
    if (isInviteSignup(membership)) {
      inviteSignups += 1;
      if (joinedToday(membership, now)) inviteSignupsToday += 1;
    }
  });

  return {
    total: users.length,
    managers,
    volunteers: users.length - managers,
    inviteSignups,
    inviteSignupsToday,
  };
};
